import { useEffect, useState } from "react";
import api, { pickErr } from "@/lib/api";

function StatCard({ label, value, testid }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5"
      data-testid={testid}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: body } = await api.get("/admin/analytics");
        if (!cancelled) setData(body?.data || null);
      } catch (err) {
        if (!cancelled) setError(pickErr(err, "Failed to load analytics"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Analytics</h2>
      <p className="text-sm text-slate-500 mb-6">
        System-wide totals and per-school activity.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p data-testid="analytics-load-error" className="text-sm text-red-600">
          {error}
        </p>
      ) : data ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard
              label="Schools"
              value={data.total_schools}
              testid="analytics-schools"
            />
            <StatCard
              label="Students"
              value={data.total_students}
              testid="analytics-students"
            />
            <StatCard
              label="Tests assigned"
              value={data.total_tests_assigned}
              testid="analytics-assigned"
            />
            <StatCard
              label="Tests completed"
              value={data.total_tests_completed}
              testid="analytics-completed"
            />
            <StatCard
              label="Reports generated"
              value={data.total_reports ?? 0}
              testid="analytics-reports"
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <section
              className="lg:col-span-2 bg-white border border-slate-200 rounded-lg"
              data-testid="analytics-school-breakdown"
            >
              <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900">
                School-wise activity
              </div>
              {Array.isArray(data.school_breakdown) &&
              data.school_breakdown.length ? (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-5 py-2 font-medium">School</th>
                      <th className="px-5 py-2 font-medium text-right">
                        Students
                      </th>
                      <th className="px-5 py-2 font-medium text-right">
                        Tests completed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.school_breakdown.map((row) => (
                      <tr
                        key={row.school_id}
                        className="border-t border-slate-100"
                      >
                        <td className="px-5 py-2 text-slate-900">
                          {row.school_name}
                        </td>
                        <td className="px-5 py-2 text-slate-700 text-right tabular-nums">
                          {row.students}
                        </td>
                        <td className="px-5 py-2 text-slate-700 text-right tabular-nums">
                          {row.tests_completed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="px-5 py-8 text-sm text-slate-500">
                  No schools registered yet.
                </p>
              )}
            </section>

            <section
              className="bg-white border border-slate-200 rounded-lg p-5"
              data-testid="analytics-most-used-category"
            >
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Most used category
              </div>
              {data.most_used_category ? (
                <>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {data.most_used_category.name}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {data.most_used_category.assignments_count} assignments
                  </div>
                </>
              ) : (
                <p className="mt-2 text-sm text-slate-500">No data yet.</p>
              )}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}
